import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';

const float = keyframes`
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-20px);
  }
`;

const fall = keyframes`
  0% {
    transform: translateY(-20px) rotate(0deg);
    opacity: 0;
  }
  10% {
    opacity: 0.1;
  }
  100% {
    transform: translateY(100vh) rotate(360deg);
    opacity: 0.1;
  }
`;

const NotFoundContainer = styled.div`
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: ${props => props.theme.colors.cream};
  position: relative;
  overflow: hidden;
`;

const ContentContainer = styled.div`
  text-align: center;
  padding: 2rem;
  position: relative;
  z-index: 2;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-radius: 20px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  
  @media (max-width: 768px) {
    padding: 1.5rem;
    margin: 0 1rem;
  }
`;

const Guitar = styled.div`
  font-size: 5rem;
  margin-bottom: 1rem;
  animation: ${float} 3s ease-in-out infinite;
  
  @media (max-width: 768px) {
    font-size: 4rem;
  }
`;

const Title = styled.h1`
  font-family: ${props => props.theme.fonts.heading};
  font-size: 8rem;
  margin-bottom: 1rem;
  color: ${props => props.theme.colors.wood};
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.2);
  
  @media (max-width: 768px) {
    font-size: 6rem;
  }
`;

const Message = styled.p`
  font-size: 1.2rem;
  margin-bottom: 2rem;
  color: ${props => props.theme.colors.text};
  line-height: 1.6;
  
  @media (max-width: 768px) {
    font-size: 1rem;
    margin-bottom: 1.5rem;
  }
`;

const HomeButton = styled(Link)`
  display: inline-block;
  padding: 12px 30px;
  background-color: ${props => props.theme.colors.wood};
  color: ${props => props.theme.colors.cream};
  text-decoration: none;
  border-radius: 50px;
  font-weight: 500;
  transition: all 0.3s ease;
  border: 2px solid transparent;

  &:hover {
    background-color: transparent;
    border-color: ${props => props.theme.colors.wood};
    color: ${props => props.theme.colors.wood};
    transform: translateY(-2px);
  }
  
  @media (max-width: 768px) {
    padding: 10px 25px;
    font-size: 0.9rem;
  }
`;

const BackgroundNotes = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 1;
`;

const Note = styled.div`
  position: fixed;
  font-size: 2rem;
  opacity: 0;
  animation: ${fall} ${props => props.duration}s linear infinite;
  animation-delay: ${props => props.delay}s;
  left: ${props => props.left}%;
  top: -20px;
  will-change: transform;
  
  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  align-items: center;
`;

const SitemapLink = styled.button`
  font-size: 0.9rem;
  color: ${props => props.theme.colors.wood};
  text-decoration: underline;
  opacity: 0.8;
  transition: all 0.3s ease;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;

  &:hover {
    opacity: 1;
    transform: translateY(-1px);
  }

  @media (max-width: 768px) {
    font-size: 0.8rem;
  }
`;

const SitemapModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(5px);
  display: ${props => props.isOpen ? 'flex' : 'none'};
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 20px;
`;

const SitemapContent = styled.div`
  background: ${props => props.theme.colors.cream};
  border-radius: 20px;
  padding: 2rem;
  max-width: 600px;
  width: 100%;
  position: relative;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  
  @media (max-width: 768px) {
    padding: 1.5rem;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: ${props => props.theme.colors.wood};
  transition: transform 0.3s ease;

  &:hover {
    transform: rotate(90deg);
  }
`;

const SitemapList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const SitemapItem = styled(Link)`
  display: flex;
  align-items: center;
  padding: 1rem;
  margin: 0.5rem 0;
  text-decoration: none;
  color: ${props => props.theme.colors.wood};
  background: rgba(139, 69, 19, 0.1);
  border-radius: 10px;
  transition: all 0.3s ease;

  &:hover {
    transform: translateX(10px);
    background: rgba(139, 69, 19, 0.2);
  }

  &::before {
    content: '🎵';
    margin-right: 1rem;
    font-size: 1.2rem;
  }
`;

const SitemapTitle = styled.h2`
  font-family: ${props => props.theme.fonts.heading};
  color: ${props => props.theme.colors.wood};
  margin-bottom: 1.5rem;
  text-align: center;
`;

function NotFound() {
  const [isSitemapOpen, setIsSitemapOpen] = useState(false);

  useEffect(() => {
    document.title = "Page non trouvée - Anna Sanz";
  }, []);

  const sitemapRoutes = [
    { path: '/', name: 'Accueil' },
    { path: '/members', name: 'Membres' },
    { path: '/music', name: 'Musique' },
    { path: '/contact', name: 'Contact' }
  ];

  const renderNotes = () => {
    const notes = ['🎵', '🎶', '🎼'];
    return Array.from({ length: 15 }, (_, i) => (
      <Note
        key={i}
        duration={Math.random() * 8 + 6}
        delay={i * 0.3}
        left={Math.random() * 100}
      >
        {notes[Math.floor(Math.random() * notes.length)]}
      </Note>
    ));
  };

  return (
    <NotFoundContainer>
      <BackgroundNotes>
        {renderNotes()}
      </BackgroundNotes>
      <ContentContainer>
        <Guitar>🎸</Guitar>
        <Title>404</Title>
        <Message>Oups ! Cette page semble s'être égarée dans une ballade...</Message>
        <ButtonContainer>
          <HomeButton to="/">Retour à l'accueil</HomeButton>
          <SitemapLink onClick={() => setIsSitemapOpen(true)}>
            Voir le plan du site
          </SitemapLink>
        </ButtonContainer>
      </ContentContainer>

      <SitemapModal isOpen={isSitemapOpen}>
        <SitemapContent>
          <CloseButton onClick={() => setIsSitemapOpen(false)}>×</CloseButton>
          <SitemapTitle>Plan du Site</SitemapTitle>
          <SitemapList>
            {sitemapRoutes.map((route) => (
              <li key={route.path}>
                <SitemapItem 
                  to={route.path}
                  onClick={() => setIsSitemapOpen(false)}
                >
                  {route.name}
                </SitemapItem>
              </li>
            ))}
          </SitemapList>
        </SitemapContent>
      </SitemapModal>
    </NotFoundContainer>
  );
}

export default NotFound;